import React from "react";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useFeatureToggle } from "@/shared/hooks/useFeatureToggle";
import { ErrorState } from "@/shared/ui/states/ErrorState";
import { LoadingState } from "@/shared/ui/states/LoadingState";
import { useAuth } from "@/shared/providers/AuthProvider";
import { AccessDeniedState } from "@/shared/ui/states/AccessDeniedState";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** API format permission from PERMISSIONS (e.g., PERMISSIONS.VEHICLE_VIEW) */
  apiPermission?: string;
  /** Multiple API permissions - user needs ANY one (OR logic) */
  apiPermissions?: string[];
  /** If true with apiPermissions, user needs ALL permissions (AND logic). Default is false (OR logic) */
  requireAll?: boolean;
  requiredFeature?: string;
  requiredRoles?: string[];
  /** Allowed user types (subcontractor field values: 0=Normal, 1=Subcontractor, 2=Funding Source, 3=Center User, 4=Ecolane) */
  allowedUserTypes?: number[];
  fallback?: React.ReactNode;
  loadingFallback?: React.ReactNode;
  /** Temporary escape hatch for frontend-defined permissions that are not yet exposed by the session API. */
  allowWithoutApiPermission?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  apiPermission,
  apiPermissions,
  requireAll = false,
  requiredFeature,
  requiredRoles,
  allowedUserTypes,
  fallback,
  loadingFallback,
  allowWithoutApiPermission = false,
}) => {
  const { loading, user } = useAuth();
  const { hasPermission, hasPermissions, hasAnyRole } = usePermissions();
  const { isFeatureEnabled } = useFeatureToggle();

  // Show loading state while auth is being resolved
  if (loading) {
    return (
      <>
        {loadingFallback || <LoadingState message="Checking permissions..." />}
      </>
    );
  }

  // Check user type restriction (if specified)
  if (allowedUserTypes && allowedUserTypes.length > 0) {
    const userType = user?.subcontractor ?? 0; // Default to Normal (0) if not set
    if (!allowedUserTypes.includes(userType)) {
      return <>{fallback || <AccessDeniedState message="Access denied. This feature is not available for your user type." showHomeLink={false} showLogoutLink={false} />}</>;
    }
  }

  // Check feature flag first (using full hierarchy)
  if (requiredFeature) {
    const featureResult = isFeatureEnabled(requiredFeature);
    if (!featureResult.enabled) {
      const defaultFallback = (
        <ErrorState
          error={`Feature not available: ${featureResult.reason}`}
          onRetry={() => window.location.reload()}
        />
      );
      return <>{fallback || defaultFallback}</>;
    }
  }

  // Check API format permissions (new format)
  if (apiPermission && !hasPermission(apiPermission)) {
    void allowWithoutApiPermission;
    return <>{fallback || <AccessDeniedState message="Access denied. You don't have permission to access this page." showHomeLink={false} showLogoutLink={false} />}</>;
  }

  // Check multiple API permissions
  if (apiPermissions && !hasPermissions(apiPermissions, requireAll)) {
    return <>{fallback || <AccessDeniedState message="Access denied. You don't have permission to access this page." showHomeLink={false} showLogoutLink={false} />}</>;
  }

  // Check roles
  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    return <>{fallback || <AccessDeniedState message="Access denied. You don't have permission to access this page." showHomeLink={false} showLogoutLink={false} />}</>;
  }

  // All checks passed, render children
  return <>{children}</>;
};
